import { Plus, User, Users } from 'lucide-react';
import { formatDate } from '../../lib/dateFormat';
import type { ProfilWahl, ProfilZeile } from '../../lib/api/stammdaten';
import { cn } from '../../lib/utils';

type Props = Readonly<{
  kundenName: string;
  profile: ProfilZeile[];
  wahl: ProfilWahl;
  onWahl: (wahl: ProfilWahl) => void;
  /** Ohne Kundenkonto gibt es noch keine Profile — nur „neues Profil". */
  disabled?: boolean;
  /**
   * Ein gerade hinzugefügtes, noch nicht gespeichertes Profil.
   *
   * Es steht als eigene Kachel neben den bestehenden, damit man sieht, dass es
   * da ist. Angelegt wird es erst, wenn der Assistent durchläuft.
   */
  entwurf?: { name: string; birthDate?: string | null } | null;
}>;

const istGewaehlt = (wahl: ProfilWahl, profil: ProfilZeile): boolean => {
  if (profil.istKonto) return wahl.art === 'konto';
  return wahl.art === 'bestehend' && wahl.subprofileId === profil.id;
};

function Kachel({
  aktiv,
  disabled,
  onClick,
  children,
}: Readonly<{ aktiv: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }>) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={aktiv}
      className={cn(
        'flex min-h-[104px] w-full flex-col items-center justify-center gap-1 rounded-xl border p-3 text-center transition',
        'hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
        aktiv ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : 'border-dashed'
      )}
    >
      {children}
    </button>
  );
}

/**
 * Die Profilauswahl als Kacheln — so, wie der Kunde sie beschrieben hat:
 * das eigene Profil, die bereits angelegten, und eines zum Hinzufügen.
 *
 * Vorher stand hier ein Suchschlitz über alle Schülerprofile des Mandanten. Wer
 * eine Anfrage vor sich hatte, sah dadurch nicht, welche Profile zu *diesem*
 * Kunden gehören — und legte im Zweifel ein zweites an.
 */
export default function ProfilKacheln({
  kundenName,
  profile,
  wahl,
  onWahl,
  disabled = false,
  entwurf = null,
}: Props) {
  const kontoProfil = profile.find((p) => p.istKonto) || null;
  const lernende = profile.filter((p) => !p.istKonto);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <Kachel
        aktiv={wahl.art === 'konto'}
        disabled={disabled}
        onClick={() => onWahl({ art: 'konto' })}
      >
        <User className="h-5 w-5 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium">
          {kontoProfil?.name || kundenName || 'Mein Profil'}
        </span>
        <span className="text-[11px] text-muted-foreground">Der Kunde selbst</span>
      </Kachel>

      {lernende.map((profil) => (
        <Kachel
          key={profil.id}
          aktiv={istGewaehlt(wahl, profil)}
          disabled={disabled}
          onClick={() => onWahl({ art: 'bestehend', subprofileId: profil.id })}
        >
          <Users className="h-5 w-5 text-muted-foreground" aria-hidden />
          <span className="text-sm font-medium">
            {profil.name || [profil.firstName, profil.lastName].filter(Boolean).join(' ')}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {profil.birthDate ? formatDate(profil.birthDate) : 'Schülerprofil'}
          </span>
        </Kachel>
      ))}

      {entwurf ? (
        <Kachel
          aktiv={wahl.art === 'neu'}
          // Erster Klick wählt das Profil, ein zweiter öffnet es zum Ändern.
          onClick={() =>
            onWahl(wahl.art === 'neu' ? { art: 'neu' } : { art: 'neu', bestaetigt: true })
          }
        >
          <Users className="h-5 w-5 text-muted-foreground" aria-hidden />
          <span className="text-sm font-medium">{entwurf.name}</span>
          <span className="text-[11px] text-muted-foreground">
            {wahl.art === 'neu' ? 'neu · zum Ändern anklicken' : 'neu · wird mit angelegt, wenn gewählt'}
          </span>
        </Kachel>
      ) : (
        <Kachel aktiv={wahl.art === 'neu'} onClick={() => onWahl({ art: 'neu' })}>
          <Plus className="h-5 w-5 text-muted-foreground" aria-hidden />
          <span className="text-sm font-medium">Neues Profil</span>
          {/* „hinzufügen" stand früher hier — das tut jetzt der Knopf im
              Formular, und die Kachel öffnet es nur. */}
          <span className="text-[11px] text-muted-foreground">Daten eingeben</span>
        </Kachel>
      )}
    </div>
  );
}
